import { Head } from '@inertiajs/react'
import { PageLayout } from '@/components/landing/PageLayout'
import { PageHeader, SectionDivider } from '@/components/landing/PageHeader'

export default function Terms() {
  return (
    <>
      <Head>
        <title>Terms & Conditions — KonexZero</title>
        <meta name="description" content="Terms of service for KonexZero's realtime voice, video, and screen sharing APIs." />
      </Head>
      <PageLayout>
      <div className="space-y-10">
        <PageHeader
          eyebrow="Legal · last updated 2026-03-12"
          title="Terms & Conditions"
        />

        <SectionDivider />

        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              By accessing or using the KonexZero platform, APIs, dashboard, documentation, or any
              related services (collectively, the "Services"), you agree to be bound by these Terms
              and Conditions. If you are using the Services on behalf of an organization, you
              represent that you have the authority to bind that organization to these terms.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">2. Description of Services</h2>
            <p className="text-muted-foreground leading-relaxed">
              KonexZero provides realtime communication APIs enabling voice calls, video
              conferencing, and screen sharing capabilities. The Services include REST APIs,
              WebSocket signaling, developer dashboard, usage analytics, and associated
              documentation.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">3. Account Registration</h2>
            <p className="text-muted-foreground leading-relaxed">
              To use the Services, you must create an account and provide accurate, complete
              information. You are responsible for:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized use</li>
              <li>
                Ensuring all users within your organization comply with these terms
              </li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">4. API Keys &amp; Credentials</h2>
            <p className="text-muted-foreground leading-relaxed">
              API keys and secrets are issued per application and must be kept confidential. You must
              not share, publish, or embed API keys in client-side code or public repositories.
              KonexZero is not responsible for unauthorized access resulting from compromised
              credentials. You may rotate keys at any time through the dashboard.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">5. Acceptable Use</h2>
            <p className="text-muted-foreground leading-relaxed">
              You agree not to use the Services to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Violate any applicable laws, regulations, or third-party rights</li>
              <li>Transmit malicious code, malware, or engage in hacking activities</li>
              <li>
                Conduct surveillance, recording, or monitoring without proper consent from all
                participants
              </li>
              <li>Generate excessive API traffic intended to disrupt the service (DoS)</li>
              <li>
                Circumvent rate limits, authentication mechanisms, or usage quotas
              </li>
              <li>Resell or redistribute the Services without written authorization</li>
              <li>Transmit illegal, harmful, or objectionable content through media streams</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">6. Service Tiers &amp; Billing</h2>
            <p className="text-muted-foreground leading-relaxed">
              KonexZero offers multiple service tiers (Free, Growth, Enterprise). Usage is measured
              in participant-minutes. Free tier usage is subject to the limits described on our
              pricing page. Paid tiers are billed monthly in advance. Overages beyond your plan's
              included minutes are billed at the applicable overage rate.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              You may upgrade, downgrade, or cancel your subscription at any time. Downgrades and
              cancellations take effect at the end of the current billing period. Refunds are
              provided only at our sole discretion.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">7. Service Level &amp; Availability</h2>
            <p className="text-muted-foreground leading-relaxed">
              We strive to maintain high availability but do not guarantee uninterrupted service.
              Scheduled maintenance windows will be communicated in advance. Enterprise customers
              with SLA agreements are governed by their specific SLA terms.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">8. Intellectual Property</h2>
            <p className="text-muted-foreground leading-relaxed">
              KonexZero retains all rights to the platform, APIs, documentation, and associated
              technology. You retain all rights to your applications and content transmitted through
              the Services. The Services grant you a limited, non-exclusive, revocable license to
              use our APIs in accordance with these terms and our documentation.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">9. Data Processing</h2>
            <p className="text-muted-foreground leading-relaxed">
              Our handling of data is governed by our{' '}
              <a href="/privacy" className="text-primary hover:underline">
                Privacy Policy
              </a>
              . By using the Services, you acknowledge that media streams are processed through
              our global edge network. You are responsible for obtaining appropriate consent
              from your end users for any data processed through the Services.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">10. Suspension &amp; Termination</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may suspend or terminate your access to the Services if you violate these terms,
              engage in abusive behavior, fail to pay applicable fees, or if required by law.
              Upon termination, your right to use the Services ceases immediately. We will make
              commercially reasonable efforts to allow you to export your data prior to deletion.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">11. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed">
              To the maximum extent permitted by law, KonexZero shall not be liable for any
              indirect, incidental, special, consequential, or punitive damages, or any loss of
              profits or revenues, whether incurred directly or indirectly. Our total aggregate
              liability for any claims arising from these terms shall not exceed the amount you
              paid us in the twelve months preceding the claim.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">12. Disclaimer of Warranties</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Services are provided "as is" and "as available" without warranties of any kind,
              whether express or implied, including but not limited to implied warranties of
              merchantability, fitness for a particular purpose, and non-infringement. We do not
              warrant that the Services will be uninterrupted, error-free, or secure.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">13. Indemnification</h2>
            <p className="text-muted-foreground leading-relaxed">
              You agree to indemnify and hold harmless KonexZero, its officers, directors,
              employees, and agents from any claims, damages, losses, and expenses (including
              reasonable attorney fees) arising from your use of the Services, your violation of
              these terms, or your violation of any third-party rights.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">14. Changes to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              We reserve the right to modify these terms at any time. Material changes will be
              communicated via email or dashboard notification at least 30 days before taking
              effect. Your continued use of the Services after changes become effective constitutes
              acceptance of the revised terms.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">15. Governing Law</h2>
            <p className="text-muted-foreground leading-relaxed">
              These terms shall be governed by and construed in accordance with applicable law.
              Any disputes arising from these terms or the Services shall be resolved through
              binding arbitration, except where prohibited by law.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">16. Contact</h2>
            <p className="text-muted-foreground leading-relaxed">
              For questions about these Terms and Conditions, please contact us at{' '}
              <a href="mailto:legal@konexzero.com" className="text-primary hover:underline">
                legal@konexzero.com
              </a>{' '}
              or visit our{' '}
              <a href="/contact" className="text-primary hover:underline">
                Contact page
              </a>
              .
            </p>
          </section>
        </div>
      </div>
    </PageLayout>
    </>
  )
}
